import React, { useState } from "react";
import {
  Container,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Stack,
  Alert,
} from "@mui/material";
import JSZip from "jszip";
import secrets from "../data/secret.json"; // Path adjusted

const LogExportViewer = ({gotoanalytics}) => {
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [exportId, setExportId] = useState("");
  const [createdExportId, setCreatedExportId] = useState("");

  const handleLanguageChange = (_, newLang) => {
    if (newLang) setLanguage(newLang);
  };

const createExport = async () => {
  setLoading(true);
  setError(null);
  setLogs([]);
  setCreatedExportId("");

  try {
    const res = await fetch("/api/d-id-export.php", {
      method: "POST",
    });

    if (!res.ok) throw new Error("Failed to create export");

    const data = await res.json();
    setExportId(data.export_id);
    setCreatedExportId(data.export_id);
  } catch (err) {
    console.error(err);
    setError("Failed to create export.");
  } finally {
    setLoading(false);
  }
};


  const createExport2 = async () => {
    setLoading(true);
    setError(null);
    setLogs([]);
    setCreatedExportId("");

    try {
      const { authorization, agent_id } = secrets;

      const res = await fetch("https://api.d-id.com/agents/chats/exports", {
        method: "POST",
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          authorization,
        },
        body: JSON.stringify({ agent_id }),
      });

      if (!res.ok) throw new Error("Failed to create export");

      const data = await res.json();
      setExportId(data.export_id);
      setCreatedExportId(data.export_id); // Store to display in UI
    } catch (err) {
      console.error(err);
      setError("Failed to create export.");
    } finally {
      setLoading(false);
    }
  };

const fetchExportById = async () => {
  if (!exportId) {
    setError("Please enter a valid export ID.");
    return;
  }

  setLoading(true);
  setError(null);
  setLogs([]);

  try {
    const res = await fetch(`/api/d-id-export.php?export_id=${encodeURIComponent(exportId)}`);

    if (!res.ok) throw new Error("Failed to fetch export");

    const data = await res.json();

    if (!data.zip_base64) {
      throw new Error(data.error || "ZIP data not ready.");
    }

    const zipBlob = new Blob([
      Uint8Array.from(atob(data.zip_base64), (c) => c.charCodeAt(0))
    ]);

    const zip = await JSZip.loadAsync(zipBlob);

    const jsonFiles = [];

    await Promise.all(
      Object.keys(zip.files).map(async (filename) => {
        if (filename.endsWith(".json")) {
          const content = await zip.files[filename].async("string");
          jsonFiles.push({ filename, content: JSON.parse(content) });
        }
      })
    );

    setLogs(jsonFiles);
  } catch (err) {
    console.error(err);
    setError("Failed to retrieve or extract logs. ZIP may not be ready yet.");
  } finally {
    setLoading(false);
  }
};


  const fetchExportById2 = async () => {
    if (!exportId) {
      setError("Please enter a valid export ID.");
      return;
    }

    setLoading(true);
    setError(null);
    setLogs([]);

    try {
      const { authorization } = secrets;

      const res = await fetch(
        `https://api.d-id.com/agents/chats/exports/${exportId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            authorization,
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch export");

      const { result } = await res.json();
      const result_url = result?.result_url;

      if (!result_url) {
        throw new Error("result_url not available yet. Try again later.");
      }

      const zipResponse = await fetch(result_url);
      const zipBlob = await zipResponse.blob();

      const zip = await JSZip.loadAsync(zipBlob);

      const jsonFiles = [];

      await Promise.all(
        Object.keys(zip.files).map(async (filename) => {
          if (filename.endsWith(".json")) {
            const content = await zip.files[filename].async("string");
            jsonFiles.push({ filename, content: JSON.parse(content) });
          }
        })
      );

      setLogs(jsonFiles);
    } catch (err) {
      console.error(err);
      setError("Failed to retrieve or extract logs. ZIP may not be ready yet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
  <Button color="inherit" onClick={gotoanalytics} sx={{ mr: 1 }}>
	Analytics
  </Button>
  <br/>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {language === "en" ? "View Chat Logs" : "Ver Registros de Chat"}
          </Typography>

          <ToggleButtonGroup
            value={language}
            exclusive
            onChange={handleLanguageChange}
            size="small"
          >
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="es">Español</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack spacing={2} sx={{ mt: 4 }}>
          <Button
            variant="contained"
            onClick={createExport}
            disabled={loading}
          >
            {language === "en" ? "Create New Export" : "Crear Nueva Exportación"}
          </Button>

          {createdExportId && (
            <Alert severity="info">
              Export created! ID: <strong>{createdExportId}</strong>
            </Alert>
          )}

          <TextField
            label="Export ID"
            value={exportId}
            onChange={(e) => setExportId(e.target.value)}
            fullWidth
          />

          <Button
            variant="outlined"
            onClick={fetchExportById}
            disabled={loading}
          >
            {language === "en" ? "Fetch Export by ID" : "Obtener Exportación por ID"}
          </Button>
        </Stack>

        {loading && (
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 3 }}>
            {error}
          </Typography>
        )}

        {logs.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6">JSON Files:</Typography>
            <List>
              {logs.map(({ filename, content }, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemText
                    primary={filename}
                    secondary={
                      <pre style={{ whiteSpace: "pre-wrap" }}>
                        {JSON.stringify(content, null, 2)}
                      </pre>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default LogExportViewer;
